import { eSketchPathType, KbVector, SketchNode, SketchPath, Token } from '@kb3d/models/classes';
import { imageTracer, ImageTracerOptionsParamers } from 'imagetracer';
import { ElementNode, parse, RootNode } from 'svg-parser';
import { Layer, PathHandler } from './path-handler';
import { projectOnNodeAxis } from './util';

interface ImageTraceColor {
    r: number;
    g: number;
    b: number;
    a: number;
}

export interface ImageTraceResult {
    layers: Layer[][];
    palette: ImageTraceColor[];
    width: number;
    height: number;
}

export class SvgConverter {
    static overwriteSketchWithSvg(node: SketchNode, data: Blob) {
        const decover = new TextDecoder();
        node.paths.clear();

        const header = data.slice(0, 100);
        header.arrayBuffer().then(buffer => {
            const headerText = decover.decode(buffer);
            if (headerText.search(/JFIF/) !== -1 || headerText.search(/PNG/) !== -1) {
                // is raster
                const dataUrl = URL.createObjectURL(data);
                const options: ImageTracerOptionsParamers = {
                    qtres: 0.1,
                    pathomit: 16,
                    numberofcolors: 2,
                };
                const nodeManager = node._manager;
                if (!nodeManager) {
                    throw Error('no node manager');
                }

                imageTracer.imageToTracedata(dataUrl, options).then((trace: ImageTraceResult) => {
                    if (trace.palette && trace.palette.length > 1) {
                        const pathParser = new PathHandler(
                            node,
                            SvgConverter.calculateScaleDivider([0, 0, trace.width, trace.height]),
                            nodeManager
                        );

                        const paletteStrengths = (trace.palette as ImageTraceColor[]).map((color, idx) => ({
                            idx,
                            strength: getPaletteColorStrength(color),
                        }));
                        paletteStrengths.sort((a, b) => a.strength - b.strength);
                        const bgIdx = paletteStrengths[0].idx;
                        trace.layers.splice(bgIdx, 1);
                        const primaryLayers = trace.layers.flat();

                        const pathsCreated: SketchPath[][] = [];

                        // Convert layers to sketchPaths
                        primaryLayers.forEach((layer, layerIndex) => {
                            const newPaths = pathParser.parseTrace(layer.segments);
                            const pathGroupKey = 'tracedPath' + layerIndex;
                            pathsCreated[layerIndex] = newPaths;
                            newPaths.forEach(path => {
                                path.pathGroupKey = pathGroupKey;
                            });
                            node.paths.push(...newPaths);
                        });

                        // Convert layers to holes
                        primaryLayers.forEach((layer, layerIndex) => {
                            if (layer.holechildren) {
                                layer.holechildren.forEach(holeLayerIdx => {
                                    const pathSet = pathsCreated[holeLayerIdx];
                                    if (pathSet) {
                                        pathSet.forEach(path => {
                                            path.holeForPathGroup = 'tracedPath' + layerIndex;
                                        });
                                    }
                                });
                            }
                        });
                    }
                });
            } else {
                data.text().then(svgText => {
                    const parsedSVG = parse(svgText);
                    SvgConverter.parseSvgRecursively(node, parsedSVG);
                });
            }
        });
    }

    static findSvgViewBox(svg: ElementNode | RootNode) {
        for (let i = 0; i < svg.children.length; i++) {
            const child = svg.children[i] as ElementNode;
            if (typeof child === 'string') {
                return undefined;
            } else if (child.tagName === 'svg' && 'properties' in child) {
                return getViewBoxFromSvgElement(child);
            }
        }

        function getViewBoxFromSvgElement(elem: ElementNode): number[] | undefined {
            if (typeof elem.properties?.viewBox === 'string' && elem.properties.viewBox) {
                return elem.properties.viewBox.split(' ').map(int => parseInt(int));
            }
            if (elem.properties?.width && elem.properties?.height) {
                const width = elem.properties.width;
                const height = elem.properties.height;
                return [
                    0,
                    0,
                    typeof width === 'string' ? parseInt(width) : width,
                    typeof height === 'string' ? parseInt(height) : height,
                ];
            }
        }
    }

    static parseSvgRecursively(node: SketchNode, svg: ElementNode | RootNode, viewBox?: number[]) {
        if (!viewBox) {
            viewBox = SvgConverter.findSvgViewBox(svg) || [0, 0, 100, 100];
        }
        if (svg.children) {
            const nodeManager = node._manager;
            if (!nodeManager) {
                throw Error('no node manager');
            }

            for (let i = 0; i < svg.children.length; i++) {
                const child = svg.children[i] as ElementNode;

                if (child.tagName === 'path' && child.properties) {
                    const pathParser = new PathHandler(node, SvgConverter.calculateScaleDivider(viewBox), nodeManager);
                    const newPaths = pathParser.parsePath(child.properties.d as string);
                    node.paths.push(...newPaths);
                    //call actual parser
                } else if (child.tagName === 'rect' && child.properties) {
                    SvgConverter.createRectangle(node, child, viewBox);
                } else if (child.tagName === 'circle' && child.properties) {
                    SvgConverter.createCircle(node, child, viewBox);
                } else if (child.tagName === 'polyline' && child.properties) {
                    SvgConverter.createPolyLine(node, child, viewBox);
                } else if (child.tagName === 'line' && child.properties) {
                    SvgConverter.createLine(node, child, viewBox);
                } else if (child.tagName === 'polygon' && child.properties) {
                    SvgConverter.createPolyLine(node, child, viewBox, true);
                }
                SvgConverter.parseSvgRecursively(node, child, viewBox);
                nameGeneratedPaths(node);
            }
        }
    }

    static removeBackGroundOnConvertedSVG(children: [ElementNode]) {
        const parent = children[0];
        const newChildren = parent.children as [ElementNode];
        const resultChildren: ElementNode[] = [];
        newChildren.forEach(child => {
            if (child && child.properties) {
                if ('opacity' in child.properties) {
                    if (child.properties.opacity == 1) {
                        resultChildren.push(child);
                    }
                }
            }
        });
        newChildren.clear();
        resultChildren.forEach(child => {
            newChildren.push(child);
        });
        // if (svg.children) {
        //     console.log(svg.children.length);
        //     for (let i = 0; i < svg.children.length; i++) {
        //         const child = svg.children[i] as ElementNode;
        //         if (child.properties) {
        //             if (child.properties.opacity) {
        //                 if (Number(child.properties.opacity) < 1) {
        //                     console.log('Not a valid path');
        //                     svg.children.remove(child);
        //                 } else {
        //                     console.log('valid path:', child);
        //                 }
        //             }
        //         }
        //         this.removeBackGroundOnConvertedSVG(child);
        //     }
        // }
    }

    // static projectSketchOnNormal(node: SketchNode) {
    //     node.paths.forEach(path => {
    //         path.controlPoints.forEach(point => {
    //             if (node.normal === eAxis.y) {
    //                 point.position = new KbVector(point.position.x, 0, point.position.y);
    //                 point.direction = new KbVector(point.direction.x, 0, point.direction.y);
    //                 if (point.direction2) {
    //                     point.direction2 = new KbVector(point.direction2.x, 0, point.direction2.y);
    //                 }
    //             } else if (node.normal === eAxis.x) {
    //                 point.position = new KbVector(0, point.position.y, point.position.x);
    //                 point.direction = new KbVector(0, point.direction.y, point.direction.x);
    //                 if (point.direction2) {
    //                     point.direction2 = new KbVector(0, point.direction2.y, point.direction2.x);
    //                 }
    //             }
    //         });
    //     });
    // }

    static calculateScaleDivider(viewbox: number[]) {
        if (viewbox) {
            const width = viewbox[2];
            const height = viewbox[3];
            if (width > height) {
                return width;
            } else {
                return height;
            }
        } else {
            return 1;
        }
    }

    static addNewPath(node: SketchNode, pathType?: eSketchPathType) {
        const nodeManager = node._manager;
        if (!nodeManager) {
            throw Error('no node manager');
        }
        const newPath = nodeManager.create(Token.SketchPath);
        if (newPath) {
            newPath.name = 'Path';
            newPath._expanded = true;
            if (pathType) {
                newPath.type = pathType;
            }
            node.paths.push(newPath);
        }
        return newPath;
    }

    static createRectangle(node: SketchNode, element: ElementNode, viewbox: number[]) {
        const nodeManager = node._manager;
        if (!nodeManager) {
            throw Error('no node manager');
        }
        const scaleDivider = SvgConverter.calculateScaleDivider(viewbox);
        if (!element.properties) {
            return;
        }
        const x = (element.properties.x as number) / scaleDivider;
        const y = ((element.properties.y as number) / scaleDivider) * -1;
        const rx = (element.properties.rx as number) / scaleDivider;
        const ry = ((element.properties.ry as number) / scaleDivider) * -1;
        const width = (element.properties.width as number) / scaleDivider;
        const height = ((element.properties.height as number) / scaleDivider) * -1;
        let newPath = SvgConverter.addNewPath(node, eSketchPathType.points);
        if (rx && ry) {
            const newControlPoint = nodeManager.create(Token.SketchControlPoint);
            newControlPoint.position = projectOnNodeAxis(node, x, y + ry);
            newPath.controlPoints.push(newControlPoint);
            const newControlPoint2 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint2.position = projectOnNodeAxis(node, x, y + height - ry);
            newPath.controlPoints.push(newControlPoint2);

            newPath = SvgConverter.addNewPath(node, eSketchPathType.quadraticBezier);
            const newControlPoint3 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint3.position = projectOnNodeAxis(node, x, y + height - ry);
            newControlPoint3.direction = projectOnNodeAxis(node, 0, ry);
            newPath.controlPoints.push(newControlPoint3);

            const newControlPoint4 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint4.position = projectOnNodeAxis(node, x + rx, y + height);
            newPath.controlPoints.push(newControlPoint4);

            newPath = SvgConverter.addNewPath(node, eSketchPathType.points);
            const newControlPoint5 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint5.position = projectOnNodeAxis(node, x + rx, y + height);
            newPath.controlPoints.push(newControlPoint5);

            const newControlPoint6 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint6.position = projectOnNodeAxis(node, width + x - rx, y + height);
            newPath.controlPoints.push(newControlPoint6);

            newPath = SvgConverter.addNewPath(node, eSketchPathType.quadraticBezier);
            const newControlPoint7 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint7.position = projectOnNodeAxis(node, width + x - rx, y + height);
            newControlPoint7.direction = projectOnNodeAxis(node, -ry, 0);
            newPath.controlPoints.push(newControlPoint7);

            const newControlPoint8 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint8.position = projectOnNodeAxis(node, width + x, y + height - ry);
            newPath.controlPoints.push(newControlPoint8);

            newPath = SvgConverter.addNewPath(node, eSketchPathType.points);
            const newControlPoint9 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint9.position = projectOnNodeAxis(node, width + x, y + height - ry);
            newPath.controlPoints.push(newControlPoint9);

            const newControlPoint10 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint10.position = projectOnNodeAxis(node, width + x, y + ry);
            newPath.controlPoints.push(newControlPoint10);

            newPath = SvgConverter.addNewPath(node, eSketchPathType.quadraticBezier);
            const newControlPoint11 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint11.position = projectOnNodeAxis(node, width + x, y + ry);
            newControlPoint11.direction = projectOnNodeAxis(node, 0, -ry);
            newPath.controlPoints.push(newControlPoint11);

            const newControlPoint12 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint12.position = projectOnNodeAxis(node, width + x - rx, y);
            newPath.controlPoints.push(newControlPoint12);

            newPath = SvgConverter.addNewPath(node, eSketchPathType.points);
            const newControlPoint13 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint13.position = projectOnNodeAxis(node, width + x - rx, y);
            newPath.controlPoints.push(newControlPoint13);

            const newControlPoint14 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint14.position = projectOnNodeAxis(node, x + rx, y);
            newPath.controlPoints.push(newControlPoint14);

            newPath = SvgConverter.addNewPath(node, eSketchPathType.quadraticBezier);
            const newControlPoint15 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint15.position = projectOnNodeAxis(node, x + rx, y);
            newControlPoint15.direction = projectOnNodeAxis(node, ry, 0);
            newPath.controlPoints.push(newControlPoint15);

            const newControlPoint16 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint16.position = projectOnNodeAxis(node, x, y + ry);
            newPath.controlPoints.push(newControlPoint16);
        } else {
            const newControlPoint = nodeManager.create(Token.SketchControlPoint);
            newControlPoint.position = projectOnNodeAxis(node, x, y);
            newPath.controlPoints.push(newControlPoint);

            const newControlPoint2 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint2.position = projectOnNodeAxis(node, x, y + height);
            newPath.controlPoints.push(newControlPoint2);

            const newControlPoint3 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint3.position = projectOnNodeAxis(node, x + width, y + height);
            newPath.controlPoints.push(newControlPoint3);

            const newControlPoint4 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint4.position = projectOnNodeAxis(node, x + width, y);
            newPath.controlPoints.push(newControlPoint4);

            const newControlPoint5 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint5.position = projectOnNodeAxis(node, x, y);
            newPath.controlPoints.push(newControlPoint5);
        }
    }

    static createCircle(node: SketchNode, element: ElementNode, viewbox: number[]) {
        const nodeManager = node._manager;
        if (!nodeManager) {
            throw Error('no node manager');
        }
        const scaleDivider = SvgConverter.calculateScaleDivider(viewbox);
        if (element.properties) {
            const cx = (element.properties.cx as number) / scaleDivider;
            const cy = ((element.properties.cy as number) / scaleDivider) * -1;
            const radius = (element.properties.r as number) / scaleDivider;

            const newPath = SvgConverter.addNewPath(node, eSketchPathType.centerRadiusCircle);
            const newControlPoint = nodeManager.create(Token.SketchControlPoint);
            newControlPoint.position = projectOnNodeAxis(node, cx, cy);
            newControlPoint.direction = new KbVector(0, 0, 1);
            newPath.controlPoints.push(newControlPoint);

            const newControlPoint2 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint2.position = projectOnNodeAxis(node, cx + radius, cy);
            newPath.controlPoints.push(newControlPoint2);
        }
    }

    static createLine(node: SketchNode, element: ElementNode, viewbox: number[]) {
        const nodeManager = node._manager;
        if (!nodeManager) {
            throw Error('no node manager');
        }
        const scaleDivider = SvgConverter.calculateScaleDivider(viewbox);
        if (element.properties) {
            const newPath = SvgConverter.addNewPath(node, eSketchPathType.points);
            const x1 = (element.properties.x1 as number) / scaleDivider;
            const y1 = ((element.properties.y1 as number) / scaleDivider) * -1;
            const x2 = (element.properties.x2 as number) / scaleDivider;
            const y2 = ((element.properties.y2 as number) / scaleDivider) * -1;

            const newControlPoint = nodeManager.create(Token.SketchControlPoint);
            newControlPoint.position = projectOnNodeAxis(node, x1, y1);
            newPath.controlPoints.push(newControlPoint);

            const newControlPoint2 = nodeManager.create(Token.SketchControlPoint);
            newControlPoint2.position = projectOnNodeAxis(node, x2, y2);
            newPath.controlPoints.push(newControlPoint2);
        }
    }

    static createPolyLine(node: SketchNode, element: ElementNode, viewbox: number[], closePath = false) {
        const nodeManager = node._manager;
        if (!nodeManager) {
            throw Error('no node manager');
        }
        const scaleDivider = SvgConverter.calculateScaleDivider(viewbox);
        if (element.properties) {
            const newPath = SvgConverter.addNewPath(node, eSketchPathType.points);
            const points = (element.properties.points as string).replace(',', ' ').split(' ');
            for (let i = 0; i < points.length; i = i + 2) {
                const newControlPoint = nodeManager.create(Token.SketchControlPoint);
                newControlPoint.position = new KbVector(
                    parseInt(points[i]) / scaleDivider,
                    (parseInt(points[i + 1]) / scaleDivider) * -1,
                    0
                );
                newPath.controlPoints.push(newControlPoint);
            }
            if (closePath) {
                const newControlPoint = nodeManager.create(Token.SketchControlPoint);
                newControlPoint.position = new KbVector(
                    parseInt(points[0]) / scaleDivider,
                    (parseInt(points[1]) / scaleDivider) * -1,
                    0
                );
                newPath.controlPoints.push(newControlPoint);
            }
        }
    }
}

function nameGeneratedPaths(node: SketchNode) {
    let counter = 0;
    node.paths.forEach(path => {
        if (counter > 0) {
            path.name = 'Path ' + counter.toString();
        }
        counter++;
    });
}

function getPaletteColorStrength(color: ImageTraceColor) {
    const saturation = Math.max(color.r, color.g, color.b) - Math.min(color.r, color.g, color.b);
    return color.a * 255 + saturation;
}
